import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PhotoCarousel from './components/PhotoCarousel';
import PropertyInfo from './components/PropertyInfo';
import AskQuestionModal from './components/AskQuestionModal';
import BookInspectionModal from './components/BookInspectionModal';
import MarketTrendSummary from '../../components/ui/MarketTrendSummary';
import AIChatPanel from './components/AIChatPanel';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { mockListings } from '../../constants/mockListings';
import { useLanguage } from '../../contexts/LanguageContext';

const ListingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const { user, userProfile, signOut } = useAuth();
  const { t } = useLanguage();

  // Prev/Next navigation state from sessionStorage
  const [searchIds, setSearchIds] = useState([]);
  const [searchUrl, setSearchUrl] = useState('/search');

  useEffect(() => {
    try {
      const ids = JSON.parse(sessionStorage.getItem('listinglens_search_ids') || '[]');
      const url = sessionStorage.getItem('listinglens_search_url') || '/search';
      setSearchIds(ids);
      setSearchUrl(url);
    } catch {
      setSearchIds([]);
    }
  }, []);

  const currentIndex = searchIds?.indexOf(id);
  const prevId = currentIndex > 0 ? searchIds?.[currentIndex - 1] : null;
  const nextId = currentIndex >= 0 && currentIndex < searchIds?.length - 1 ? searchIds?.[currentIndex + 1] : null;

  const handleNavProperty = (targetId) => {
    navigate(`/listing/${targetId}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const mockUser = {
    id: user?.id || 1,
    name: userProfile?.full_name || user?.email?.split('@')?.[0] || 'User',
    email: user?.email || '',
    role: userProfile?.role || 'buyer'
  };

  useEffect(() => {
    fetchListing();
  }, [id]);

  const mockListing = {
    id: '1',
    properties: {
      address: '42 Harbour Street',
      suburb: 'Sydney',
      state: 'NSW',
      postcode: '2000',
      type: 'house',
      beds: 3,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 1850000,
    description_short: 'Stunning harbour views with modern finishes throughout',
    description_long: 'This exceptional property offers breathtaking harbour views and has been meticulously renovated with premium finishes throughout. The open-plan living area flows seamlessly to a spacious entertaining deck, perfect for enjoying Sydney\'s lifestyle. The gourmet kitchen features stone benchtops and European appliances. Three generous bedrooms include a master suite with ensuite and walk-in robe. Additional features include ducted air conditioning, security parking for two cars, and storage. Located in a prime position close to cafes, restaurants, and public transport.',
    media: [
    { id: '1', original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_15d655a5d-1772108899226.png", type: 'photo', order_index: 0, alt: 'Modern Sydney house exterior with harbour views' },
    { id: '2', original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_11df829d5-1772108900447.png", type: 'photo', order_index: 1, alt: 'Bright open-plan living room with harbour views' },
    { id: '3', original_url: "https://images.unsplash.com/photo-1722764375583-146a5bc8d39c", type: 'photo', order_index: 2, alt: 'Modern kitchen with stone benchtops and stainless appliances' },
    { id: '4', original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1fbe90607-1772108900468.png", type: 'photo', order_index: 3, alt: 'Master bedroom with ensuite and harbour views' },
    { id: '5', original_url: "https://images.unsplash.com/photo-1664939404003-6ba1c2f4a31f", type: 'photo', order_index: 4, alt: 'Outdoor entertaining deck overlooking Sydney Harbour' }]

  };

  const fetchListing = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('listings')?.select(`
          *,
          properties:property_id (*),
          media (*)
        `)?.eq('id', id)?.single();

      if (error) throw error;
      setListing(data);
    } catch (error) {
      console.error('Error fetching listing:', error);
      // Look up the correct listing by ID from mock data
      const foundMock = mockListings?.find((l) => l?.id === id);
      setListing(foundMock || mockListings?.[0]);
    } finally {
      setLoading(false);
    }
  };

  const displayListing = listing || mockListing;
  const isUAE = displayListing?.region === 'uae';

  const formatPrice = (price) => {
    if (!price) return 'Contact for price';
    if (isUAE) {
      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 0
      })?.format(price);
    }
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    })?.format(price);
  };

  const handleLogout = () => {
    navigate('/authentication');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>);

  }

  return (
    <>
      <Helmet>
        <title>{displayListing?.properties?.address} - ListingLens</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header user={mockUser} onLogout={handleLogout} />
        
        <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Back + Prev/Next navigation bar */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              iconName="ArrowLeft"
              onClick={() => navigate(searchUrl)}>
              {t('backToSearch')}
            </Button>

            {searchIds?.length > 1 && currentIndex >= 0 &&
            <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground mr-1">
                  {currentIndex + 1} / {searchIds?.length}
                </span>
                <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                disabled={!prevId}
                onClick={() => prevId && handleNavProperty(prevId)}>
                  {t('previous')}
                </Button>
                <Button
                variant="outline"
                size="sm"
                iconName="ChevronRight"
                disabled={!nextId}
                onClick={() => nextId && handleNavProperty(nextId)}>
                  {t('next2')}
                </Button>
              </div>
            }
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <PhotoCarousel media={displayListing?.media || []} />
              
              <PropertyInfo listing={displayListing} />
            </div>

            <div className="lg:col-span-1">
              <div className="bg-card rounded-xl border border-border p-6 sticky top-6">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {formatPrice(displayListing?.price_or_rent)}
                  </h3>
                  <p className="text-sm text-muted-foreground">
{displayListing?.listing_type === 'rent' ? isUAE ? t('perYear') : t('perWeek') : ''}
                  </p>
                </div>

                <div className="space-y-3">
                  <Button
                    variant="default"
                    fullWidth
                    iconName="MessageSquare"
                    onClick={() => setShowQuestionModal(true)}>
                    
                    {t('askQuestion')}
                  </Button>
                  
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Calendar"
                    onClick={() => setShowInspectionModal(true)}>
                    
                    {t('bookInspection')}
                  </Button>
                </div>

                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="User" size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{t('contactAgent')}</p>
                      <p className="text-sm text-muted-foreground">{t('availableSevenDays')}</p>
                    </div>
                  </div>
                  <Button variant="outline" fullWidth iconName="Phone">
                    {t('callAgent')}
                  </Button>
                </div>
              </div>

              <div className="mt-6">
                <MarketTrendSummary
                  suburb={displayListing?.properties?.suburb}
                  state={displayListing?.properties?.state}
                  city={displayListing?.properties?.suburb}
                  propertyPrice={displayListing?.price_or_rent}
                  propertyType={displayListing?.properties?.type} />
                
              </div>

              <div className="mt-6">
                <AIChatPanel listing={displayListing} />
              </div>
            </div>
          </div>

          {/* Bottom Prev/Next navigation */}
          {searchIds?.length > 1 && currentIndex >= 0 &&
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-border">
              <div>
                {prevId && (() => {
                const prevListing = mockListings?.find((l) => l?.id === prevId);
                return (
                  <button
                    onClick={() => handleNavProperty(prevId)}
                    className="flex items-center gap-3 group text-left">
                      <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center group-hover:bg-muted transition-colors">
                        <Icon name="ChevronLeft" size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('previousProperty')}</p>
                        <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                          {prevListing?.properties?.address || t('previous')}
                        </p>
                      </div>
                    </button>);

              })()}
              </div>
              <div className="text-right">
                {nextId && (() => {
                const nextListing = mockListings?.find((l) => l?.id === nextId);
                return (
                  <button
                    onClick={() => handleNavProperty(nextId)}
                    className="flex items-center gap-3 group text-right">
                      <div>
                        <p className="text-xs text-muted-foreground">{t('nextProperty')}</p>
                        <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                          {nextListing?.properties?.address || t('next2')}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center group-hover:bg-muted transition-colors">
                        <Icon name="ChevronRight" size={20} />
                      </div>
                    </button>);

              })()}
              </div>
            </div>
          }
        </main>
      </div>
      {showQuestionModal &&
      <AskQuestionModal
        listing={displayListing}
        onClose={() => setShowQuestionModal(false)} />

      }
      {showInspectionModal &&
      <BookInspectionModal
        listing={displayListing}
        onClose={() => setShowInspectionModal(false)} />

      }
    </>);


};

export default ListingDetail;